import React, { useCallback, useEffect, useState } from "react";
import {
    Alert, Box, Button, CircularProgress, FormControlLabel, IconButton, Radio, RadioGroup, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { es } from "date-fns/locale";
import workShiftServices from "app/services/automations/work-shift-services";
import { requestErrors } from "app/services/automations/generated-file-services";
import { SaveButton } from "app/shared/ui";

const EMPTY_SHIFT = { name: "", start: "08:00", end: "16:00" };

export const describeShifts = (shifts = []) =>
    shifts.map((shift) => `${shift.name} ${shift.start}–${shift.end}`).join(" · ");

// «HH:MM» ↔ Date para el TimePicker, en 24 horas como el resto de la aplicación.
const timeToDate = (time) => {
    const [hours, minutes] = `${time || ""}`.split(":").map(Number);
    return Number.isInteger(hours) && Number.isInteger(minutes) ? new Date(2000, 0, 1, hours, minutes) : null;
};

const dateToTime = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
    return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
};

// Columnas fijas, iguales en todas las filas: el nombre no pasa de 280 px y cada hora tiene 160 px. «Termina al día
// siguiente» va bajo «Hasta», así que no corre los campos. En pantallas chicas, el nombre arriba y las horas debajo.
const ROW_SX = {
    display: "grid",
    columnGap: 1,
    rowGap: 1,
    alignItems: "start",
    gridTemplateColumns: { xs: "minmax(0, 1fr) minmax(0, 1fr) 40px", sm: "minmax(0, 280px) 160px 160px 40px" },
    gridTemplateAreas: { xs: `"name name remove" "start end ."`, sm: `"name start end remove"` },
};

const ShiftTimePicker = ({ label, value, onChange, disabled, helperText }) => (
    <TimePicker
        label={label}
        ampm={false}
        value={timeToDate(value)}
        disabled={disabled}
        // Mientras se escribe, una hora incompleta no borra la que había.
        onChange={(date) => {
            const time = dateToTime(date);
            if (time) onChange(time);
        }}
        slotProps={{
            textField: {
                size: "small", fullWidth: true, helperText,
                // En una línea desde sm; en pantallas chicas, donde la columna es más angosta, puede cortarse en dos.
                FormHelperTextProps: { sx: { mx: 0.5, whiteSpace: { sm: "nowrap" } } },
            },
        }}
    />
);

const ShiftRow = ({ shift, onChange, onRemove, disabled }) => {
    const overnight = !!shift.start && !!shift.end && shift.end <= shift.start;
    return (
        <Box sx={ROW_SX}>
            <TextField size="small" label="Nombre" value={shift.name} disabled={disabled} fullWidth sx={{ gridArea: "name" }}
                       onChange={(event) => onChange({ ...shift, name: event.target.value })} />
            <Box sx={{ gridArea: "start" }}>
                <ShiftTimePicker label="Desde" value={shift.start} disabled={disabled} onChange={(start) => onChange({ ...shift, start })} />
            </Box>
            <Box sx={{ gridArea: "end" }}>
                <ShiftTimePicker label="Hasta" value={shift.end} disabled={disabled} onChange={(end) => onChange({ ...shift, end })}
                                 helperText={overnight ? "Termina al día siguiente" : undefined} />
            </Box>
            {!disabled && (
                <Box sx={{ gridArea: "remove", pt: 0.5 }}>
                    <Tooltip title="Quitar turno">
                        <IconButton size="small" onClick={onRemove}><DeleteOutlineOutlinedIcon fontSize="small" /></IconButton>
                    </Tooltip>
                </Box>
            )}
        </Box>
    );
};

// Turnos con que se agrupan las lecturas en los reportes. Sin storeId edita los de la empresa; con storeId deja elegir
// entre los de la empresa y unos propios del local, que los reemplazan completos.
const WorkShiftsEditor = ({ storeId = null, onSaved }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [saved, setSaved] = useState(false);
    const [companyShifts, setCompanyShifts] = useState([]);
    const [shifts, setShifts] = useState([]);
    const [ownShifts, setOwnShifts] = useState(false);
    const [canEdit, setCanEdit] = useState(false);

    const apply = useCallback((data) => {
        setCompanyShifts(data?.company || []);
        setCanEdit(!!data?.can_edit);
        if (storeId) {
            setOwnShifts(data?.source === "store");
            setShifts(data?.source === "store" ? data.store : (data?.company || []));
        } else {
            setShifts(data?.company || []);
        }
    }, [storeId]);

    useEffect(() => {
        setLoading(true);
        workShiftServices.get(storeId).then(apply).catch(() => setError("No se pudieron cargar los turnos.")).finally(() => setLoading(false));
    }, [storeId, apply]);

    const save = async () => {
        setSaving(true);
        setError(null);
        setSaved(false);
        try {
            const data = storeId && !ownShifts
                ? await workShiftServices.useCompanyShifts(storeId)
                : await workShiftServices.save(storeId, shifts.map(({ name, start, end }) => ({ name, start, end })));
            apply(data);
            setSaved(true);
            onSaved?.(data);
        } catch (requestError) {
            setError(requestErrors(requestError, "No se pudieron guardar los turnos."));
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <Box sx={{ py: 2, textAlign: "center" }}><CircularProgress size={22} /></Box>;

    const editing = !storeId || ownShifts;
    return (
        <Stack spacing={1.5}>
            {!!storeId && (
                <RadioGroup value={ownShifts ? "store" : "company"} onChange={(event) => {
                    const own = event.target.value === "store";
                    setOwnShifts(own);
                    if (own && !shifts.length) setShifts(companyShifts.length ? companyShifts : [EMPTY_SHIFT]);
                }}>
                    <FormControlLabel value="company" control={<Radio size="small" />} disabled={!canEdit} label={
                        <Typography variant="body2">
                            Usar los turnos de la empresa{companyShifts.length ? `: ${describeShifts(companyShifts)}` : " (la empresa todavía no tiene turnos)"}
                        </Typography>
                    } />
                    <FormControlLabel value="store" control={<Radio size="small" />} disabled={!canEdit}
                                      label={<Typography variant="body2">Turnos propios de este local</Typography>} />
                </RadioGroup>
            )}
            {editing && (
                // En pantallas chicas cada turno ocupa dos líneas: más espacio entre turnos que dentro de uno.
                <Stack spacing={{ xs: 2.5, sm: 1.5 }}>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                        {shifts.map((shift, index) => (
                            <ShiftRow
                                key={`shift-${index}`} shift={shift} disabled={!canEdit}
                                onChange={(value) => setShifts(shifts.map((item, position) => (position === index ? value : item)))}
                                onRemove={() => setShifts(shifts.filter((item, position) => position !== index))}
                            />
                        ))}
                    </LocalizationProvider>
                    {!shifts.length && <Typography variant="body2" color="text.secondary">Sin turnos.</Typography>}
                    {canEdit && (
                        <Box>
                            <Button size="small" startIcon={<AddOutlinedIcon />} onClick={() => setShifts([...shifts, EMPTY_SHIFT])}>Agregar turno</Button>
                        </Box>
                    )}
                </Stack>
            )}
            <Typography variant="caption" color="text.secondary">
                Un turno que termina al día siguiente (22:00 a 06:00) pertenece al día en que empieza. Los turnos no se pueden solapar,
                pero no tienen que cubrir las 24 horas.
            </Typography>
            {!!error && <Alert severity="error">{error}</Alert>}
            {saved && !error && <Alert severity="success">Turnos guardados.</Alert>}
            {canEdit && (
                <Box>
                    <SaveButton loading={saving} disabled={saving} onClick={save}>Guardar turnos</SaveButton>
                </Box>
            )}
        </Stack>
    );
};

export default WorkShiftsEditor;
